import { useReadableProductName } from '@hotjar/webapp/domains/common/product/hooks/useReadableProductName';
import { getProductBySku } from '@hotjar/webapp/domains/common/product/utils';
import { useProductCatalog } from '@hotjar/webapp/domains/common/product/hooks/useProductCatalog';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { capitalize } from '@hotjar/insights-common/lib/string';

import { usePlanComparisonFeatureGate } from './usePlanComparisonFeatureGate';
import { usePlanComparisonPlanRecommendation } from './usePlanComparisonPlanRecommendation';
import type { PlanComparisonReferrer } from '../types';

interface UseModalTitleProps {
  referrer: PlanComparisonReferrer;
  initialRecommendedPlanId?: string | null;
  isUpgradeRequest?: boolean;
}

export const useModalTitle = ({
  referrer,
  initialRecommendedPlanId,
  isUpgradeRequest,
}: UseModalTitleProps): string => {
  const { t } = useTranslationWithPrefix('revenue:plan-comparison-modal');

  const { data: productCatalog } = useProductCatalog();
  const featureGate = usePlanComparisonFeatureGate(referrer);
  const productName = useReadableProductName(featureGate?.sku);
  const recommendedPlanId = usePlanComparisonPlanRecommendation({
    initialRecommendedPlanId,
    featureGate,
  });
  const hasFeatureGate = !!featureGate;
  const recommendedProduct = recommendedPlanId
    ? getProductBySku(recommendedPlanId, productCatalog)
    : null;

  let title = t('title-generic');

  if (hasFeatureGate && featureGate?.modalControls?.titleKey) {
    title = t(`${featureGate.modalControls.titleKey}`);
  } else if (isUpgradeRequest && recommendedProduct) {
    title = t('title-upgrade-request', {
      product: productName,
      tier: capitalize(recommendedProduct.tier),
    });
  } else if (hasFeatureGate && featureGate.minimumFeatureGroup) {
    title = t('title-contextual', {
      product: productName,
      tier: capitalize(featureGate.minimumFeatureGroup),
    });
  }

  return title;
};
